import type { ComponentType, ReactNode, SVGProps } from "react";

import { cx } from "@/lib/cx";
import { KanbanIcon, StatsIcon, TasksIcon, WorkoutIcon } from "@/ui/icons";
import { TONE_FILL, type Tone } from "../tones";

export type CompactSurface = "tasks" | "board" | "workout" | "focus";

/* -------------------------------------------------------------------------- */
/* Surface glyphs                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The navigation glyph for each surface, as a component rather than a
 * pre-sized element — this system sizes a glyph at the call site (14px in a
 * row, 16px in a stat cell) and the old `SURFACE_GLYPH` maps baked `size-4`
 * into the node.
 */
export const SURFACE_ICON: Record<
  CompactSurface,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  tasks: TasksIcon,
  board: KanbanIcon,
  workout: WorkoutIcon,
  focus: StatsIcon,
};

/**
 * A 6px status mark — rule 4's cheapest channel.
 *
 * Non-text, so `TONE_FILL`'s vivid accent is legal here (it is measured at the
 * 3:1 tint threshold, which it clears on every surface in the app). Always
 * `aria-hidden`: a dot never carries information that is not also written
 * beside it.
 */
export function Dot({ tone, className }: { tone: Tone; className?: string }) {
  return (
    <span
      aria-hidden
      className={cx("busee-dot size-1.5 shrink-0 rounded-none", TONE_FILL[tone], className)}
    />
  );
}

/**
 * The bare 16/600 section line.
 *
 * Use it only where a region needs rule 6's label without a panel around it;
 * inside a {@link Panel}, the panel head already owns this role.
 */
export function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("px-1 text-section text-subtle", className)}>{children}</div>;
}

/*
 * There is deliberately no badge component here. `ui/Badge` is 20–24px with a
 * soft tinted fill, which at this density is a second colour block per row —
 * exactly what rule 4 exists to prevent — and a compact outlined version of it
 * was written, found to have no call site the plain right-aligned value could
 * not serve better, and deleted rather than left in the vocabulary unused.
 */
